import React from 'react';
import { Home, Flame, Image as ImageIcon } from 'lucide-react';
import { C, useFetch, fmtValue, confidenceStyle } from '../shared';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const FACTS = ['land_area_m2', 'planning_zone', 'dwelling_type', 'year_built', 'floor_area_m2', 'storeys', 'heritage_listed', 'distance_to_coast_m'];
const HAZARDS = ['flood_band', 'bushfire_prone', 'wind_region', 'coastal_inundation', 'earthquake_zone', 'arpc_wind_band'];

const Img = ({ title, url, meta, testid }) => (
  <div className="rounded-sm overflow-hidden border border-[rgba(136,150,168,0.2)]" data-testid={testid}>
    {url ? <img src={`${BACKEND}${url}`} alt={title} className="w-full h-32 object-cover" />
      : <div className="h-32 flex items-center justify-center text-[10px] px-2 text-center" style={{ color: C.slate }}>{meta?.note || meta?.source || 'not available'}</div>}
    <p className="px-2 py-1 text-[9px] flex justify-between" style={{ background: C.bg, color: C.slate }}><span className="font-semibold uppercase">{title}</span><span className="truncate ml-2">{meta?.source}</span></p>
  </div>
);

const Fact = ({ name, s, testid }) => (
  <div className="flex items-start justify-between gap-2 text-[11px] border-t border-[rgba(136,150,168,0.1)] py-1" data-testid={testid}>
    <span style={{ color: C.slate }}>{name.replace(/_/g, ' ')}</span>
    <span className="text-right">
      <span className="font-mono" style={{ color: C.navy }}>{fmtValue(s?.value)}</span>
      <span className="ml-1.5 text-[8px] font-semibold px-1 py-0.5 rounded uppercase" style={confidenceStyle(s?.confidence)}>{(s?.confidence || '').replace('_', ' ')}</span>
    </span>
  </div>
);

export const PropertyCard = ({ api, pid, rebuild }) => {
  const enc = encodeURIComponent(pid);
  const [img] = useFetch(api, `/property/${enc}/imagery`, [pid]);
  const [profile] = useFetch(api, `/property/${enc}/profile`, [pid]);
  const [hz] = useFetch(api, `/property/${enc}/hazards`, [pid]);
  return (
    <div className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white p-3" data-testid="rj-property-card">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="grid grid-cols-2 gap-2 lg:col-span-1">
          <Img title="Aerial" url={img?.aerial_url} meta={img?.aerial} testid="rj-property-aerial" />
          <Img title="Map" url={img?.static_map_url} meta={img?.static_map} testid="rj-property-map" />
          <p className="col-span-2 text-[9px] flex items-center gap-1" style={{ color: C.slate }}><ImageIcon className="w-3 h-3" /> {img ? `signed links · ${img.street_view?.note || ''}` : 'loading imagery…'}</p>
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1" style={{ color: C.blue }}><Home className="w-3 h-3" /> What the registers say</p>
          {!profile && <p className="text-[10px] py-2" style={{ color: C.slate }}>asking the parcel, zone, heritage and coast layers…</p>}
          {profile && FACTS.map(f => profile[f] && <Fact key={f} name={f} s={profile[f]} testid={`rj-fact-${f}`} />)}
          {profile && <p className="text-[9px] mt-1" style={{ color: C.slate }} data-testid="rj-profile-counts">{profile.counts.populated} of {profile.counts.fields} fields from free data · the rest name the licensed source that would</p>}
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1" style={{ color: C.blue }}><Flame className="w-3 h-3" /> Hazard bands</p>
          {!hz && <p className="text-[10px] py-2" style={{ color: C.slate }}>asking the flood, bushfire and storm tide layers…</p>}
          {hz && HAZARDS.map(f => hz[f] && <Fact key={f} name={f} s={hz[f]} testid={`rj-hazard-${f}`} />)}
          {rebuild && (
            <div className="mt-2 rounded-sm border border-dashed p-2" style={{ borderColor: 'rgba(178,117,22,0.5)' }} data-testid="rj-rebuild-prefill">
              <p className="text-[9px] uppercase tracking-wider" style={{ color: C.amber }}>{rebuild.watermark}</p>
              <p className="text-sm font-semibold" style={{ color: C.navy }}>${Number(rebuild.estimate).toLocaleString()} <span className="text-[10px] font-normal" style={{ color: C.slate }}>rebuild · range ${Number(rebuild.low).toLocaleString()}–${Number(rebuild.high).toLocaleString()}</span></p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
